import type { ContactCreateInput } from "src/api/generated/erp/contacts/model/contactCreateInput.ts";
import type { Contact } from "src/api/generated/erp/db/types/tables/contact.ts";
import type { CustomerAddress } from "src/api/generated/erp/db/types/tables/customerAddress.ts";
import type { CustomerFormValues } from "src/components/views/erp/sales/customer/CustomerFormContent.tsx";
import type { CustomerOfferFormValues } from "src/components/views/erp/sales/customerOrderOffer/CustomerOfferFormContent.tsx";
import type { DeepPartial } from "react-hook-form";
import { DEFAULT_COUNTRY_CODE } from "src/components/common/country/countries.ts";

export const getCustomerContactsForSubmit = ({
	contacts,
	defaultContactIndex,
}: Pick<CustomerFormValues, "contacts" | "defaultContactIndex">): ContactCreateInput[] => {
	if (defaultContactIndex == null || defaultContactIndex < 0) return contacts ?? [];

	return (contacts ?? []).map((contact, index) => ({
		...contact,
		isDefaultContact: index === defaultContactIndex,
	}));
};

/**
 * Carries the sales lead contacts over to the new customer, keeping the default contact selection. Customer contacts
 * require a name, so a contact without one is named by its email or phone. Contacts without any of these are skipped.
 */
export const getCustomerContactsFromSalesLeadContacts = (
	salesLeadContacts: Contact[],
): Pick<CustomerFormValues, "contacts" | "defaultContactIndex"> => {
	const namedContacts = salesLeadContacts
		.map((contact) => ({ ...contact, name: [contact.name, contact.email, contact.phone].find(isNonBlank) ?? "" }))
		.filter((contact) => contact.name !== "");
	return {
		contacts: namedContacts.map((contact) => ({
			name: contact.name,
			designation: contact.designation,
			phone: contact.phone,
			email: contact.email,
			note: contact.note,
			isContractSigner: contact.isContractSigner,
			isDefaultContact: false,
		})),
		defaultContactIndex: namedContacts.findIndex((contact) => contact.isDefaultContact),
	};
};

function isNonBlank(value: string): boolean {
	return value.trim() !== "";
}

export const getCustomerInvoiceAddressForSubmit = ({
	invoiceSameAsDelivery,
	deliveryAddress,
	invoiceAddress,
}: Pick<CustomerFormValues, "invoiceSameAsDelivery" | "deliveryAddress" | "invoiceAddress">): CustomerAddress => {
	return invoiceSameAsDelivery ? deliveryAddress : invoiceAddress;
};

export const getOfferAddressValuesFromCustomerAddress = (
	address: DeepPartial<CustomerAddress>,
): DeepPartial<CustomerOfferFormValues> => ({
	deliveryAddressId: undefined,
	deliveryName: address.name ?? "",
	deliveryRecipientName: address.recipientName ?? "",
	deliveryAddress_1: address.address_1 ?? "",
	deliveryAddress_2: address.address_2 ?? "",
	deliveryPostalCode: address.postalCode ?? "",
	deliveryCity: address.city ?? "",
	deliveryCountry: address.country ?? DEFAULT_COUNTRY_CODE,
	deliveryContact: [address.contact, address.phone].filter(Boolean).join(" "),
	contactName: address.contact ?? "",
	contactEmail: address.email ?? "",
	contactPhone: address.phone ?? "",
});
